import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './onboardingTour.css';
import confetti from 'canvas-confetti';
import { TOUR_PENDING_KEY } from './tours';

const SHOW_BUTTONS = ['next', 'previous', 'close'];

const ONBOARDING_IS_ADMIN_KEY = 'onboardingIsAdmin';

function fireConfetti() {
  const burst = (angle, origin) =>
    confetti({ angle, spread: 60, particleCount: 80, origin, zIndex: 9999 });
  burst(60,  { x: 0, y: 0.65 });
  burst(120, { x: 1, y: 0.65 });
  setTimeout(() => {
    burst(80,  { x: 0.2, y: 0.5 });
    burst(100, { x: 0.8, y: 0.5 });
  }, 150);
}

// Shown on the home page after all tour stages complete
export function showTourComplete() {
  let d;
  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    steps: [
      {
        popover: {
          title: 'Tour Complete!',
          description:
            "You've seen the key features of Showroom Pricing. Explore at your own pace — and if you ever want to revisit this tour, find it in the navigation menu under What's New.",
          nextBtnText: 'Done',
          onNextClick: () => {
            d.destroy();
            fireConfetti();
          },
        },
      },
    ],
  });
  d.drive();
}

// Stage 1 — nav menu (no element highlight so the open dropdown isn't obscured)
export function startOnboardingTour(history, isAdmin, setMenuOpen) {
  sessionStorage.setItem(ONBOARDING_IS_ADMIN_KEY, isAdmin ? '1' : '0');
  const total = isAdmin ? 7 : 4;
  let d;
  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    steps: [
      {
        popover: {
          title: `Navigation (1 of ${total})`,
          description:
            'Your navigation menu is now open in the top-right corner. Everything in the app lives here — Product Search, QR settings, Admin tools, Lead Management, and your account — organized into clear sections. You\'ll also find Feature Requests, where you can vote on and suggest new features, and Help, a searchable library of walkthrough videos.',
        },
      },
      {
        element: '#tour-header-search',
        onHighlightStarted: () => setMenuOpen(false),
        popover: {
          title: `Header Search (1 of ${total})`,
          description:
            'The search bar in the banner is a quick way to search your product catalog from anywhere in the app without leaving the current page.',
          side: 'bottom',
          nextBtnText: 'Next →',
          onNextClick: () => {
            d.destroy();
            if (window.location.pathname === '/home') {
              setTimeout(() => resumeOnboardingHome(history), 300);
            } else {
              sessionStorage.setItem(TOUR_PENDING_KEY, 'onboarding-home');
              history.push('/home');
            }
          },
        },
      },
    ],
  });
  setMenuOpen(true);
  d.drive();
}

// Stage 2 — Home page (4 steps)
export function resumeOnboardingHome(history) {
  const isAdmin = sessionStorage.getItem(ONBOARDING_IS_ADMIN_KEY) === '1';
  const total = isAdmin ? 7 : 4;
  let d;
  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    steps: [
      {
        element: '#tour-home',
        popover: {
          title: `Home (2 of ${total})`,
          description:
            'The Home page is your starting point each time you log in. It shows bulletins, announcements, pricing updates, and your showroom activity stats.',
          side: 'bottom',
        },
      },
      {
        element: '#tour-pricing-updates',
        popover: {
          title: `Product Updates (2 of ${total})`,
          description:
            'The B2B Files tab shows when and which distributor has sent updated product and pricing data. Note: B2B files only appear if Showroom Pricing is your B2B data provider. The Manual Files tab keeps a history of price lists you have uploaded directly.',
          side: 'right',
        },
      },
      {
        element: '#tour-product-stats',
        popover: {
          title: `Product Statistics (2 of ${total})`,
          description:
            'See how many QR code scans have happened in your showroom and slice the data by date, product, or sales rep to understand showroom traffic and product interest. For a single at-a-glance view, open the new Scan Dashboard from the navigation menu — it combines KPIs, trends, the view → checkout funnel, an activity heatmap, and more on one filterable page.',
          side: 'left',
        },
      },
      {
        element: '#tour-scan-qr',
        popover: {
          title: `QR Scanner (2 of ${total})`,
          description:
            "Use this button to open your device's camera and scan a QR code from a printed price tag. It will take you straight to that product's page — and from there, Checkout lets you send a customer home with several samples at once, scanning each extra board without re-entering their details.",
          side: 'top',
          align: 'end',
          nextBtnText: 'Next →',
          onNextClick: () => {
            d.destroy();
            sessionStorage.setItem(TOUR_PENDING_KEY, 'onboarding-products');
            history.push('/search');
          },
        },
      },
    ],
  });
  d.drive();
}

// Stage 3 — Product Search (2 Driver.js steps + fullscreen image slideshow)
export function resumeOnboardingProducts(history) {
  const isAdmin = sessionStorage.getItem(ONBOARDING_IS_ADMIN_KEY) === '1';
  const total = isAdmin ? 7 : 4;

  let d;
  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    steps: [
      {
        element: '#tour-field-search',
        popover: {
          title: `Search (3 of ${total})`,
          description:
            'Search your catalog by any combination of fields — Manufacturer, Style, SKU, Color and more. Open "Advanced Search" for the less-used columns, and start any value with "-" to exclude it (e.g. "-gray"). Prefer a single box? The "Super Search" tab searches every field at once.',
          side: 'bottom',
        },
      },
      {
        // These controls live inside an expanded result row, which only exists
        // after a search — so this step describes them against the search form
        // rather than highlighting an element that isn't on screen yet.
        element: '#tour-field-search',
        popover: {
          title: `Managing a Product (3 of ${total})`,
          description:
            'Once you have results, expand any row with the arrow on the left to see every option the product sells in. From there you can set retail price overrides — one SKU at a time or one price across all of them with "Apply to all" — and, if you are an admin or read/write user, discontinue the whole product or just a single color or size. To print labels, tick the products you want and use the bar above the results to choose how many copies of each to queue.',
          side: 'bottom',
        },
      },
      {
        element: '#tour-search-header',
        popover: {
          title: `Print Queue (3 of ${total})`,
          description:
            'Use the Print Queue button to collect QR code labels for multiple products and print them all at once.',
          side: 'bottom',
          align: 'end',
          nextBtnText: 'Next →',
          onNextClick: () => {
            d.destroy();
            sessionStorage.setItem(TOUR_PENDING_KEY, 'onboarding-dup-product');
            history.push('/tour/products');
          },
        },
      },
    ],
  });
  d.drive();
}

// Stage 4 — Settings (admin only)
export function resumeOnboardingSettings(history) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    steps: [
      {
        element: '#tour-qr-settings',
        popover: {
          title: 'Settings (4 of 7)',
          description:
            'The Settings page is where you configure QR code labels — colors, dot styles, logo, and price-based color rules. Product Page Settings controls what customers see when they scan, including whether they see prices at all or a coloured price tier instead. Other settings like logo links, UOM codes, material classes, and the company default timezone live here too (each user can pick their own timezone on their profile page).',
          side: 'right',
          nextBtnText: 'Next →',
          onNextClick: () => {
            d.destroy();
            sessionStorage.setItem(TOUR_PENDING_KEY, 'onboarding-admin');
            history.push('/admin');
          },
        },
      },
    ],
  });
  d.drive();
}

// Stage 5 — Admin tabs (admin only), steps through each tab
export function resumeOnboardingAdmin(history) {
  const tabs = [
    {
      value: 'users',
      label: 'Users',
      desc: 'Manage user accounts, reset passwords, and assign roles and locations to your team members.',
    },
    {
      value: 'announcement',
      label: 'Announcements',
      desc: 'Post time-sensitive messages that appear on the Home page for all users — great for staff updates or temporary notices.',
    },
    {
      value: 'bulletin',
      label: 'Bulletins',
      desc: 'Create persistent messages that stay on the Home page until removed. Use these for ongoing reminders or policies.',
    },
    {
      value: 'CompanyMargin',
      label: 'Cost Adjustments',
      desc: 'Set company-wide margin or markup rules to adjust the cost shown on price tags, and manage Cost Adjustment Rules — scoped freight/landing adjustments and margins by manufacturer, distributor, role, material class, or size. Record vendor Freight & Tariff Rates here too (fuel surcharges, delivery fees, per-unit freight) — display-only charges staff see on products. On sites using the custom calculation engine, this page also has a collapsible step-by-step summary of exactly how your prices are built, with a dropdown to preview and switch between your available pricing models.',
    },
    {
      value: 'DisclaimerUpdate',
      label: 'Disclaimers',
      desc: 'Edit the legal disclaimer text that prints at the bottom of your QR coded product scan page.',
    },
  ];
  let d;
  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    steps: tabs.map((tab, i) => ({
      element: `#wrapped-tab-${tab.value}`,
      onHighlightStarted: () => {
        document.getElementById(`wrapped-tab-${tab.value}`)?.click();
      },
      popover: {
        title: `Admin: ${tab.label} (5 of 7)`,
        description: tab.desc,
        ...(i === tabs.length - 1 && {
          nextBtnText: 'Next →',
          onNextClick: () => {
            d.destroy();
            sessionStorage.setItem(TOUR_PENDING_KEY, 'onboarding-lead');
            history.push('/lead-management');
          },
        }),
      },
    })),
  });
  d.drive();
}

// Stage 6 — Lead Management
export function resumeOnboardingLead(history) {
  const isAdmin = sessionStorage.getItem(ONBOARDING_IS_ADMIN_KEY) === '1';
  const stageNum = isAdmin ? 6 : 4;
  const total = isAdmin ? 7 : 4;
  let d;
  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    steps: [
      {
        element: '#tour-lead-management',
        popover: {
          title: `Lead Management (${stageNum} of ${total})`,
          description:
            'Track potential customers who visit your showroom. Log contact details, follow-up dates, and the products they expressed interest in — all in one place.',
          side: 'bottom',
          nextBtnText: isAdmin ? 'Next →' : 'Finish Tour',
          onNextClick: () => {
            d.destroy();
            sessionStorage.removeItem(ONBOARDING_IS_ADMIN_KEY);
            if (isAdmin) {
              sessionStorage.setItem(TOUR_PENDING_KEY, 'onboarding-importexport');
              history.push('/importexport');
            } else {
              sessionStorage.setItem(TOUR_PENDING_KEY, 'tour-complete');
              history.push('/home');
            }
          },
        },
      },
    ],
  });
  d.drive();
}

// Stage 7 — Import / Export (admin only)
export function resumeOnboardingImportExport(history) {
  let d;
  d = driver({
    allowClose: true,
    showButtons: SHOW_BUTTONS,
    steps: [
      {
        element: '#tour-importexport',
        popover: {
          title: 'Import / Export (7 of 7)',
          description:
            'The Price List Import tab lets you bulk-upload new pricing spreadsheets. The File Export tab lets you download your current product and pricing data as a spreadsheet.',
          side: 'bottom',
          nextBtnText: 'Finish Tour',
          onNextClick: () => {
            d.destroy();
            sessionStorage.setItem(TOUR_PENDING_KEY, 'tour-complete');
            history.push('/home');
          },
        },
      },
    ],
  });
  d.drive();
}
